import { Trade, TradeType, TokenAmount, CurrencyAmount } from '@uniswap/sdk'
import React, { useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Field } from '../../state/swap/actions'
import { useUserSlippageTolerance } from '../../state/user/hooks'
import { TYPE } from '../../theme'
import { computeSlippageAdjustedAmounts, computeTradePriceBreakdown } from '../../utils/prices'
import { getTaxAdjustedAmounts } from '../../hooks/Trades'
import { AutoColumn } from '../Column'
import QuestionHelper from '../QuestionHelper'
import { RowBetween, RowFixed } from '../Row'
import FormattedPriceImpact from './FormattedPriceImpact'
import { SectionBreak } from './styleds'
import SwapRoute from './SwapRoute'

function TradeSummary({ trade, allowedSlippage }: { trade: Trade; allowedSlippage: number }) {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()
  const { priceImpactWithoutFee, realizedLPFee } = computeTradePriceBreakdown(trade)
  const isExactIn = trade.tradeType === TradeType.EXACT_INPUT
  
  // Get original slippage-adjusted amounts
  const originalSlippageAdjustedAmounts = computeSlippageAdjustedAmounts(trade, allowedSlippage)
  
  // Get tax-adjusted amounts
  const taxAdjustedAmounts = getTaxAdjustedAmounts(trade)
  
  // Apply tax adjustments to slippage-adjusted amounts if we have tax tokens
  let finalSlippageAdjustedAmounts = originalSlippageAdjustedAmounts
  
  if (taxAdjustedAmounts.adjustedOutputAmount && taxAdjustedAmounts.adjustedOutputAmount !== trade.outputAmount) {
    // Calculate the tax ratio
    const taxRatio = parseFloat(taxAdjustedAmounts.adjustedOutputAmount.divide(trade.outputAmount).toSignificant(10))
    
    // Apply the same ratio to slippage-adjusted output
    const originalSlippageOutput = originalSlippageAdjustedAmounts[Field.OUTPUT]
    if (originalSlippageOutput) {
      // Create a new CurrencyAmount with the adjusted raw value
      const adjustedRawValue = originalSlippageOutput.raw.toString()
      const adjustedBigNumber = Math.floor(parseFloat(adjustedRawValue) * taxRatio)
      
      const adjustedSlippageOutput = originalSlippageOutput instanceof TokenAmount
        ? new TokenAmount(originalSlippageOutput.token, adjustedBigNumber.toString())
        : CurrencyAmount.ether(adjustedBigNumber.toString())
      
      finalSlippageAdjustedAmounts = {
        ...originalSlippageAdjustedAmounts,
        [Field.OUTPUT]: adjustedSlippageOutput
      }
    }
  }
  
  console.log('TradeSummary slippage calculation:', {
    originalOutput: trade.outputAmount.toSignificant(6),
    taxAdjustedOutput: taxAdjustedAmounts.adjustedOutputAmount?.toSignificant(6),
    originalSlippageOutput: originalSlippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(6),
    finalSlippageOutput: finalSlippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(6),
    allowedSlippage
  })
  
  const slippageAdjustedAmounts = finalSlippageAdjustedAmounts

  return (
    <>
      <AutoColumn style={{ padding: '0 20px' }}>
        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {isExactIn ? t('minimumReceived') : t('maximumSold')}
            </TYPE.black>
            <QuestionHelper text={t('slippageHelper')} />
          </RowFixed>
          <RowFixed>
            <TYPE.black color={theme.text1} fontSize={14}>
              {isExactIn
                ? `${slippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(4)} ${trade.outputAmount.currency.symbol}` ??
                  '-'
                : `${slippageAdjustedAmounts[Field.INPUT]?.toSignificant(4)} ${trade.inputAmount.currency.symbol}` ??
                  '-'}
            </TYPE.black>
          </RowFixed>
        </RowBetween>
        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {t('priceImpact')}
            </TYPE.black>
            <QuestionHelper text={t('priceImpactHelper')} />
          </RowFixed>
          <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
        </RowBetween>

        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {t('liquidityProviderFee')}
            </TYPE.black>
            <QuestionHelper text={t('liquidityProviderFeeHelper')} />
          </RowFixed>
          <TYPE.black fontSize={14} color={theme.text1}>
            {realizedLPFee ? `${realizedLPFee.toSignificant(4)} ${trade.inputAmount.currency.symbol}` : '-'}
          </TYPE.black>
        </RowBetween>
      </AutoColumn>
    </>
  )
}

export interface AdvancedSwapDetailsProps {
  trade?: Trade
}

export function AdvancedSwapDetails({ trade }: AdvancedSwapDetailsProps) {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()

  const [allowedSlippage] = useUserSlippageTolerance()

  const showRoute = Boolean(trade && trade.route.path.length > 2)

  return (
    <AutoColumn gap="md">
      {trade && (
        <>
          <TradeSummary trade={trade} allowedSlippage={allowedSlippage} />
          {showRoute && (
            <>
              <SectionBreak />
              <AutoColumn style={{ padding: '0 24px' }}>
                <RowFixed>
                  <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
                    {t('route')}
                  </TYPE.black>
                  <QuestionHelper text={t('routeHelper')} />
                </RowFixed>
                <SwapRoute trade={trade} />
              </AutoColumn>
            </>
          )}
        </>
      )}
    </AutoColumn>
  )
}
